import React, { useEffect, useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from './ConfirmationModal';
import { ExclamationTriangleIcon } from './icons';

// 30 Minuten in Millisekunden
const TIMEOUT_MS = 30 * 60 * 1000;
// Warnung nach 25 Minuten
const WARNING_MS = 25 * 60 * 1000;

export const AutoLogout: React.FC = () => {
    const { currentUser, logout } = useAuth();
    const { t } = useTranslation();
    const [showWarning, setShowWarning] = useState(false);
    const [minutesLeft, setMinutesLeft] = useState(5);

    const handleLogout = useCallback(async () => {
        console.log("AutoLogout: Session timeout reached. Signing out.", new Date().toISOString());
        localStorage.removeItem('lastActivity'); // Clean up
        await logout();
        window.location.reload(); // Reload to clear any memory states
    }, [logout]);

    const handleExtendSession = useCallback(() => {
        const now = Date.now().toString();
        localStorage.setItem('lastActivity', now);
        setShowWarning(false);
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        // SSO users bypass local AutoLogout; IdP manages session length.
        const isSsoUser = currentUser.providerData.some(
            p => p.providerId?.startsWith('saml.') || p.providerId?.startsWith('oidc.')
        );

        if (isSsoUser) {
            console.log("AutoLogout: SSO login detected. Bypassing local inactivity timer.");
            return;
        }

        // CRITICAL FIX: Check if session expired while away BEFORE resetting
        const lastActivityIdx = localStorage.getItem('lastActivity');

        if (lastActivityIdx) {
            const lastActivityTime = parseInt(lastActivityIdx, 10);

            // Check if this is a fresh login that happened AFTER the last activity record
            const lastSignInTime = currentUser?.metadata.lastSignInTime
                ? new Date(currentUser.metadata.lastSignInTime).getTime()
                : 0;

            const now = Date.now();

            if (lastSignInTime > lastActivityTime) {
                console.log("AutoLogout: Fresh login detected. Resetting activity timer.");
                localStorage.setItem('lastActivity', now.toString());
            }
            else if (now - lastActivityTime > TIMEOUT_MS) {
                console.log("AutoLogout: Session expired while closed/inactive. Signing out immediately.");
                handleLogout();
                return; // Stop here, do not set up new listeners
            }
        }

        // Initialize/Update activity
        localStorage.setItem('lastActivity', Date.now().toString());

        // Events that reset the timer
        const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

        let lastEventTime = 0;

        // Event Listeners for Activity
        const handleActivity = () => {
             const now = Date.now();
             if (now - lastEventTime > 5000) { // Throttle 5 seconds
                 lastEventTime = now;
                 localStorage.setItem('lastActivity', now.toString());
                 // If the warning modal is open, ANY general interaction outside it 
                 // (or bubbling through it) implicitly extends the session
                 setShowWarning((prev) => {
                     if (prev) return false;
                     return prev;
                 });
             }
        };

        // Add listeners
        events.forEach(event => {
            window.addEventListener(event, handleActivity);
        });

        // Interval check
        const interval = setInterval(() => {
            const lastActivity = localStorage.getItem('lastActivity');
            if (lastActivity) {
                const idleTime = Date.now() - parseInt(lastActivity, 10);
                if (idleTime >= TIMEOUT_MS) {
                    handleLogout();
                } else if (idleTime >= WARNING_MS) {
                    const left = Math.ceil((TIMEOUT_MS - idleTime) / 60000);
                    setMinutesLeft(left);
                    setShowWarning(true);
                } else {
                    setShowWarning(false);
                }
            }
        }, 10000); // Check every 10s

        return () => {
            clearInterval(interval);
            events.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
        };
    }, [handleLogout, currentUser]);

    if (!showWarning) return null;

    return (
        <ConfirmationModal
            isOpen={showWarning}
            onClose={handleLogout}
            onConfirm={handleExtendSession}
            title={t('common.sessionWarning.title', 'Ihre Sitzung läuft bald ab')}
            message={t('common.sessionWarning.message', { count: minutesLeft, minutes: minutesLeft })}
            confirmButtonText={t('common.sessionWarning.extendButton', 'Angemeldet bleiben')}
            cancelButtonText={t('common.sessionWarning.logoutButton', 'Jetzt abmelden')}
            confirmButtonClass="bg-blue-600 hover:bg-blue-700"
            icon={ExclamationTriangleIcon}
            iconClassName="text-amber-600"
            iconBgClassName="bg-amber-100"
            zIndex={100}
        />
    );
};
